<template>
  <form
    class="form-time-range"
    v-on:submit.prevent="onSubmit"
  >
    <div class="form-time-range__row">
      <time-autocomplete
        class="form-time-range__input"
        placeholder="С"
        v-bind:timeline="fromTimeline"
        v-on:change="isFromChanged = true"
        v-model="from" />
      —
      <time-autocomplete
        class="form-time-range__input"
        placeholder="По"
        v-bind:timeline="toTimeline"
        v-on:change="isToChanged = true"
        v-model="to" />
    </div>
    <div class="preset-duration">
      <button
        class="preset-duration__item"
        v-for="(preset, key) in presetsDuration"
        v-bind:key="`preset-duration-${key}`"
        type="button"
        v-bind:class="{ selected: isPresetSelected(preset.value) }"
        v-on:click="onSelectPresetDuration(preset.value)"
      >
        {{ preset.title }}
      </button>
    </div>

  </form>
</template>
<script>
import { builtInPopoverMixin } from '@/mixins/builtInPopoverMixin';
import TimeAutocomplete from '@/components/TimeAutocomplete.vue';

/**
 * @description Компонент формы временного диапазона дня.
 * @type {Vue}
 */
export default {
  components: {
    TimeAutocomplete,
  },
  computed: {
    /**
     * @description Временная шкала для выбора начала времени визита.
     * @type {{title: string, datetime: string}[]}
     */
    fromTimeline() {
      const inputs = this.presetFromTimeline.length > 0
        ? this.presetFromTimeline.slice()
        : this.timeline.slice();
      if (this.from && this.addInitial) {
        inputs.unshift({ datetime: '', title: this.from });
      }
      return inputs.sort((first, second) => first.title.localeCompare(second.title));
    },
    /**
     * @description Кнопка подтверждения деактивирована.
     * @type {boolean}
     */
    isSubmitDisabled() {
      return !this.to || !this.from || this.from >= this.to;
    },
    /**
     * @description Временная шкала для выбора окончания времени визита.
     * @type {{title: string, datetime: string}[]}
     */
    toTimeline() {
      const inputs = this.presetToTimeline.length > 0
        ? this.presetToTimeline.slice()
        : this.timeline.slice();
      if (this.to && this.addInitial) {
        inputs.unshift({ datetime: '', title: this.to });
      }
      return inputs
        .concat(this.timeline.filter((item) => item.title > this.from))
        .sort((first, second) => first.title.localeCompare(second.title));
    },
  },
  data() {
    return {
      from: this.initialFrom || this.$options.placeholderValue,
      to: this.initialTo || this.$options.placeholderValue,
      isFromChanged: false,
      isToChanged: false,
      selectedPreset: null,
    };
  },
  maskTime: '##:##',
  methods: {
    onSubmit() {
      const needToClose = this.isFromChanged && this.isToChanged && !this.isSubmitDisabled;
      this.$emit(
        'submit',
        this.from,
        this.to,
        needToClose ? this.closePopover : () => {},
      );
      if (needToClose) {
        this.resetChangedValues();
      }
    },
    /**
     * @description Обработать выбор пресета.
     * @param {string} presetValue
     * @returns {void}
     */
    onSelectPresetDuration(presetValue) {
      if (!this.from) {
        return;
      }
      const presetInMinutes = this.toMinutes(presetValue);
      const fromInMinutes = this.toMinutes(this.from);
      const toInMinutes = fromInMinutes + presetInMinutes;
      const toHours = Math.floor(toInMinutes / 60);
      const toMinutes = toInMinutes % 60;
      const toTime = `${String(toHours).padStart(2, '0')}:${String(toMinutes).padStart(2, '0')}`;
      const maxTime = this.toTimeline[this.toTimeline.length - 1].title;
      this.to = toTime > maxTime ? maxTime : toTime;
      this.isToChanged = true;
      this.onSubmit();
    },
    /**
     * @description Пресет выбран.
     * @param {string} presetValue
     * @returns {boolean}
     */
    isPresetSelected(presetValue) {
      if (!this.from || !this.to) return false;
      const fromInMinutes = this.toMinutes(this.from);
      const toInMinutes = this.toMinutes(this.to);
      const presetInMinutes = this.toMinutes(presetValue);
      const difference = toInMinutes - fromInMinutes;
      return difference === presetInMinutes;
    },
    /**
     * @description В минуты.
     * @param hoursAndMinutes
     * @returns {number}
     */
    toMinutes(hoursAndMinutes) {
      const [hours, minutes] = hoursAndMinutes.split(':').map(Number);
      return hours * 60 + minutes;
    },
    /**
     * @description Сдвинуть значение "до" к следующему большему за данным.
     * @param {string} from Опорное значение.
     * @returns {void}
     */
    shiftToToNextHigherTime(from) {
      this
        .toTimeline
        .flatMap(({ title }) => (title > from ? [title] : []))
        .sort((value) => Number(value < from))
        .slice(0, 1)
        .forEach((value) => { this.to = value; });
    },
    resetChangedValues() {
      this.isFromChanged = false;
      this.isToChanged = false;
    },
  },
  mixins: [
    builtInPopoverMixin,
  ],
  name: 'FormAppointmentTimeRange',
  placeholderValue: '00:00',
  props: {
    /**
     * @description Время начала.
     * @type {string}
     */
    initialFrom: { type: String, default: '' },
    /**
     * @description Время окончания.
     * @type {string}
     */
    initialTo: { type: String, default: '' },
    /**
     * @description Временная шкала.
     * @type {any[]}
     */
    timeline: { type: [Object, Array], required: true },
    presetFromTimeline: { default: () => [], type: Array },
    presetToTimeline: { default: () => [], type: Array },
    /**
     * @description Добавлять начальные данные.
     * @type {Boolean}
     */
    addInitial: {
      default: () => true,
      type: Boolean,
    },
    /**
     * @description Заданные продолжительности.
     * @type {Array}
     */
    presetsDuration: { default: () => [], type: Array },
  },
  watch: {
    from(updatedValue) {
      this.shiftToToNextHigherTime(updatedValue);
      this.onSubmit();
    },
    to() {
      this.onSubmit();
    },
    initialFrom(updatedValue) {
      this.from = updatedValue;
    },
    initialTo(updatedValue) {
      this.to = updatedValue;
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
$button-font-size: 14px;
$button-height: 37px;
$button-width: 100%;
.form-time-range {
  padding: 22px;
  width: 300px;
  max-width: 99%;
  &__row {
    display: grid;
    grid-template-columns: 1fr 10px 1fr;
    grid-gap: 10px;
    align-items: center;
  }
  &__input {
    box-sizing: border-box;
    appearance: textfield;
    color: #707683;
    font-size: 16px;
    &:focus {
      color: #192a3e;
      background-color: #fff;
      border: 1px solid #0489e0 !important;
      outline: 0;
    }
    &:disabled {
      background-color: #fff;
    }
  }
  &__buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 6px;
    justify-content: flex-end;
    margin: 30px 0 0 0;
  }
  &__cancel {
    @include buttons.btn-transparent($button-width, $button-height, $button-font-size);
  }
  &__submit {
    @include buttons.btn-blue($button-width, $button-height, $button-font-size);
    &:disabled {
       background-color: var(--unfocus-color);
    }
  }
}

.preset-duration {
  display: flex;
  flex-flow: row wrap;
  gap: 8px;
  margin: 5px 0 0 0;
  &__item {
    background-color: #0489e024;
    border-radius: 24px;
    color: var(--action-color);
    font-size: 14px;
    font-weight: 500;
    line-height: 17px;
    padding: 0 8px 2px 8px;
    white-space: nowrap;
    &.selected,
    &:hover {
      background-color: #357ebd;
      color: #FFFFFF;
    }
  }
}
</style>
